import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingModal({ isOpen, onClose }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    project_type: "",
    budget_range: "",
    additional_info: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error("Vælg venligst en ønsket dato");
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      
      await axios.post(`${API}/booking`, bookingData);
      toast.success("Din booking er modtaget! Vi kontakter dig snarest for at bekræfte.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        project_type: "",
        budget_range: "",
        additional_info: "",
      });
      setDate(null);
      onClose();
    } catch (error) {
      toast.error("Der opstod en fejl. Prøv venligst igen.");
      console.error("Booking form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    { value: "komplet", label: "Komplet Renovering" },
    { value: "kokken", label: "Køkken" },
    { value: "badevaerelse", label: "Badeværelse" },
    { value: "maling", label: "Maling & Finish" },
    { value: "gulve", label: "Gulve" },
    { value: "andet", label: "Andet" },
  ];

  const budgetRanges = [
    { value: "under-50k", label: "Under 50.000 kr" },
    { value: "50k-100k", label: "50.000 - 100.000 kr" },
    { value: "100k-250k", label: "100.000 - 250.000 kr" },
    { value: "250k-500k", label: "250.000 - 500.000 kr" },
    { value: "over-500k", label: "Over 500.000 kr" },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-foreground/60 backdrop-blur-sm z-50"
            onClick={onClose}
            data-testid="booking-modal-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-4 top-4 md:inset-x-auto md:top-8 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-xl bg-white z-50 overflow-y-auto max-h-[90vh]"
            data-testid="booking-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-border px-4 py-3 flex items-center justify-between z-10">
              <div>
                <h2 
                  className="text-xl font-bold text-foreground"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Book Konsultation
                </h2>
                <p className="text-xs text-muted-foreground">
                  Gratis og uforpligtende
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-secondary transition-colors"
                data-testid="booking-modal-close"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 space-y-4" data-testid="booking-form">
              {/* Personal Info */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Personlige oplysninger
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="booking-name" className="text-xs">Navn *</Label>
                    <Input
                      id="booking-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1 rounded-none h-9"
                      placeholder="Dit fulde navn"
                      data-testid="booking-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-phone" className="text-xs">Telefon *</Label>
                    <Input
                      id="booking-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-1 rounded-none h-9"
                      placeholder="+45 12 34 56 78"
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="booking-email" className="text-xs">Email *</Label>
                    <Input
                      id="booking-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1 rounded-none h-9"
                      placeholder="din@email.dk"
                      data-testid="booking-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-address" className="text-xs">Adresse *</Label>
                    <Input
                      id="booking-address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="mt-1 rounded-none h-9"
                      placeholder="Gadenavn 123, 2100 Kbh"
                      data-testid="booking-address-input"
                    />
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Projektdetaljer
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Projekttype *</Label>
                    <Select
                      value={formData.project_type}
                      onValueChange={(value) => handleSelectChange("project_type", value)}
                      required
                    >
                      <SelectTrigger className="mt-1 rounded-none h-9" data-testid="booking-project-type">
                        <SelectValue placeholder="Vælg type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Budget (estimat)</Label>
                    <Select
                      value={formData.budget_range}
                      onValueChange={(value) => handleSelectChange("budget_range", value)}
                    >
                      <SelectTrigger className="mt-1 rounded-none h-9" data-testid="booking-budget">
                        <SelectValue placeholder="Vælg budget" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((range) => (
                          <SelectItem key={range.value} value={range.value}>
                            {range.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Ønsket dato *</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-1 rounded-none h-9 justify-start text-left font-normal text-sm"
                          data-testid="booking-date-picker"
                        >
                          <Calendar className="mr-2 h-3 w-3" />
                          {date ? format(date, "dd/MM/yy", { locale: da }) : "Vælg dato"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100]" align="start" side="top" sideOffset={5}>
                        <CalendarComponent
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label htmlFor="booking-info" className="text-xs">Yderligere oplysninger</Label>
                  <Textarea
                    id="booking-info"
                    name="additional_info"
                    value={formData.additional_info}
                    onChange={handleChange}
                    rows={2}
                    className="mt-1 rounded-none resize-none text-sm"
                    placeholder="Fortæl os mere om dit projekt..."
                    data-testid="booking-info-input"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none py-4"
                data-testid="booking-submit-btn"
              >
                {isSubmitting ? (
                  "Sender..."
                ) : (
                  <>
                    Book Konsultation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>

              <p className="text-[10px] text-center text-muted-foreground">
                Ved at indsende bekræfter du, at vi må kontakte dig.
              </p>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
